import type {
  PatientMessageChannel,
  PatientMessageKind,
  PatientMessageReason,
  PatientMessageStatus,
  PatientMessagingChannelConsentState,
  PatientMessagingDecisionSource,
  PatientMessagingDecisionValue,
  SchedulingErrorDetail,
} from "@emr/contracts";

/*
 * The words staff read about a patient's texts and emails (PA-7), shared by the booking's
 * messages panel, the schedule pill, Attention and the intake review. Plain words only: no
 * provider names, error codes or "outbox". A message never names the patient's reason for the
 * visit, so nothing here does either.
 */

export const PATIENT_MESSAGE_KIND_LABELS: Readonly<Record<PatientMessageKind, string>> = {
  confirmation: "Confirmation",
  reminder: "Reminder",
  intake_link: "Questionnaire link",
  manage_link: "Manage link",
  // PA-9's notices; the lower-case label also names them in "turned … messages off".
  cancellation: "Cancellation",
  reschedule: "Change of time",
  document_ready: "Document ready",
};

export const PATIENT_MESSAGE_CHANNEL_LABELS: Readonly<Record<PatientMessageChannel, string>> = {
  sms: "Text",
  email: "Email",
};

/** The status chip on a message's row in the panel. */
export const PATIENT_MESSAGE_STATUS_LABELS: Readonly<Record<PatientMessageStatus, string>> = {
  withheld: "Not sent",
  queued: "Queued",
  sending: "Sending",
  uncertain: "May not have been sent",
  sent: "Sent",
  delivered: "Delivered",
  failed: "Not delivered",
  cancelled: "Cancelled",
  stopped: "Stopped",
};

/** The quieter lower-case status words of the intake review's "Agreement and messages". */
export const PATIENT_MESSAGE_STATUS_WORDS: Readonly<Record<PatientMessageStatus, string>> = {
  withheld: "not sent",
  queued: "queued",
  sending: "sending",
  uncertain: "may not have been sent",
  sent: "sent",
  delivered: "delivered",
  failed: "not delivered",
  cancelled: "cancelled",
  stopped: "stopped (number replied STOP)",
};

/** The chip's tone: amber needs a person, blue is fine, grey is the rules working. */
export function patientMessageStatusTone(
  status: PatientMessageStatus,
  reason: PatientMessageReason | null,
): "attention" | "done" | "muted" | "progress" {
  switch (status) {
    case "failed":
      return "attention";
    case "withheld":
      return reason === "invalid_recipient" ? "attention" : "muted";
    case "uncertain":
    case "stopped":
    case "cancelled":
      return "muted";
    case "sent":
    case "delivered":
      return "done";
    case "queued":
    case "sending":
      return "progress";
  }
}

const contactWord = (channel: PatientMessageChannel) =>
  channel === "sms" ? "phone number" : "email address";
const serviceWord = (channel: PatientMessageChannel) =>
  channel === "sms" ? "text message service" : "email service";

/** Why a message was not sent, or not delivered, in one sentence. */
export function patientMessageReasonText(
  reason: PatientMessageReason,
  channel: PatientMessageChannel,
  kind: PatientMessageKind,
): string {
  switch (reason) {
    case "not_offered":
      return "The clinic does not send texts or emails yet.";
    case "channel_off":
      return channel === "sms" ? "The clinic turned texts off." : "The clinic turned emails off.";
    case "kind_off":
      return `The clinic turned ${PATIENT_MESSAGE_KIND_LABELS[kind].toLowerCase()} messages off.`;
    case "no_consent":
      return "The patient has not agreed to texts and emails, or said no.";
    case "no_recipient":
      return `The ${kind === "document_ready" ? "chart" : "booking"} has no ${contactWord(channel)}.`;
    case "invalid_recipient":
      if (kind === "document_ready")
        return `The chart's ${contactWord(channel)} is not valid. Correct it in Demographics.`;
      return channel === "sms"
        ? "The booking's phone number cannot take a text (it is not a North American number)."
        : "The booking's email address is not a valid address.";
    case "expired":
      return "It could not be sent in time, so it was not sent.";
    case "invalid_number":
      return "The number is not a valid phone number.";
    case "not_mobile":
      return "The number cannot take texts (it may be a landline).";
    case "unreachable":
      return "The phone was off or out of reach, or the number is not in service.";
    case "carrier_filtered":
      return "The patient's phone company filtered the text.";
    case "blocked":
      return "Texts to this number are blocked.";
    case "provider_rejected":
      return `The ${serviceWord(channel)} refused it.`;
    case "configuration":
      return `The clinic's ${channel === "sms" ? "text" : "email"} sender is not set up correctly. Tell a clinic administrator.`;
    case "policy_blocked":
      return "This test setup sends only to approved test recipients.";
    case "retry_exhausted":
      return `The ${serviceWord(channel)} kept asking to wait, and every try was used.`;
    case "unconfirmed":
      return `The ${serviceWord(channel)} never confirmed it was sent.`;
    case "appointment_changed":
      if (kind === "document_ready")
        return "The link was revoked or the document replaced before it was sent.";
      return "The booking changed before it was sent.";
    case "opted_out":
      return "This number replied STOP to the clinic.";
  }
}

/** What a status without a reason means, when it needs saying. */
export function patientMessageStatusText(
  status: PatientMessageStatus,
  channel: PatientMessageChannel,
): string | null {
  switch (status) {
    case "uncertain":
      return `The ${serviceWord(channel)} did not confirm it. It is never sent twice by itself; it settles within 30 minutes.`;
    case "sent":
      return channel === "email"
        ? "The email service accepted it."
        : "The text message service accepted it.";
    default:
      return null;
  }
}

/**
 * A booking row's message pill (appointmentRowState) and Attention's row title: what went wrong
 * with which channel, in the fewest words.
 */
export function patientMessageProblemLabel(
  status: "failed" | "withheld" | "stopped",
  channel: PatientMessageChannel,
): { readonly label: string; readonly shortLabel: string } {
  if (status === "stopped") {
    return channel === "sms"
      ? { label: "Texts stopped", shortLabel: "Texts stopped" }
      : { label: "Emails stopped", shortLabel: "Emails stopped" };
  }
  if (status === "withheld") {
    return channel === "sms"
      ? { label: "Check phone number", shortLabel: "Check phone" }
      : { label: "Check email address", shortLabel: "Check email" };
  }
  return channel === "sms"
    ? { label: "Text not delivered", shortLabel: "Not delivered" }
    : { label: "Email not delivered", shortLabel: "Not delivered" };
}

/**
 * Why the server refused a resend or a "Mark done", in words for the desk, shown on the message's
 * row. Undefined for details these actions never return: the caller shows the server's own
 * (PHI-free) message instead.
 */
export function patientMessageRefusalText(
  detail: SchedulingErrorDetail | undefined,
  channel: PatientMessageChannel,
): string | undefined {
  switch (detail) {
    case "message_in_flight":
      return `A ${channel === "sms" ? "text" : "email"} like this is still being sent, or may have been. Wait for it to settle before sending another.`;
    case "message_not_resendable":
      return "This booking is no longer ahead, so nothing can be resent.";
    case "message_changed":
      return "This message changed since you opened it. The list now shows it as it is.";
    case "message_consent_missing":
      return "The patient has not agreed to texts and emails for this visit, or said no.";
    case "message_recipient_stopped":
      return "Texts to this number were stopped: the number replied STOP. Only the patient can restart them, by replying START to the clinic's number.";
    case "message_recipient_missing":
      return `The booking has no ${contactWord(channel)}. Correct the contact, then resend.`;
    case "message_recipient_invalid":
      return channel === "sms"
        ? "The booking's phone number cannot take a text. Correct the contact, then resend."
        : "The booking's email address is not valid. Correct the contact, then resend.";
    case "messaging_not_offered":
      return `This clinic does not send this message by ${channel === "sms" ? "text" : "email"}.`;
    case "message_resend_limit":
      return "This booking's messages were resent the most times allowed. Call the patient instead.";
    default:
      return undefined;
  }
}

/*
 * A patient's agreement to texts and emails (PA-8): the state per channel and who decided it.
 * "Said no" is the desk's own phrase for a refusal; a STOP is about a number, never the patient's
 * care.
 */

/** Each channel in a sentence ("texts", "emails"). */
export const PATIENT_MESSAGE_CHANNEL_PLURALS: Readonly<Record<PatientMessageChannel, string>> = {
  sms: "texts",
  email: "emails",
};

/** The chip on a channel's consent row. */
export const MESSAGING_CONSENT_STATE_LABELS: Readonly<
  Record<PatientMessagingChannelConsentState, string>
> = {
  accepted: "Agreed",
  declined: "Said no",
  not_asked: "Not asked",
  stopped: "Stopped",
  chart_inactive: "Chart inactive",
};

/** Agreed is fine (blue); everything else is the rules working (grey). */
export function messagingConsentStateTone(
  state: PatientMessagingChannelConsentState,
): "done" | "muted" {
  return state === "accepted" ? "done" : "muted";
}

const SOURCE_PHRASES: Readonly<Record<PatientMessagingDecisionSource, string>> = {
  online_booking: "online when booking",
  intake_link: "in the pre-visit questionnaire",
  front_desk: "at the front desk",
  phone: "on the phone",
};

/** "Agreed on the phone" / "Said no online when booking". */
export function messagingDecisionPhrase(
  decision: PatientMessagingDecisionValue,
  source: PatientMessagingDecisionSource,
): string {
  return `${decision === "accepted" ? "Agreed" : "Said no"} ${SOURCE_PHRASES[source]}`;
}

/** What a channel's state means, when the chip alone does not say it. */
export function messagingConsentStateText(
  state: PatientMessagingChannelConsentState,
  channel: PatientMessageChannel,
): string | null {
  switch (state) {
    case "not_asked":
      return `Not asked yet. No ${PATIENT_MESSAGE_CHANNEL_PLURALS[channel]} are sent until the patient agrees.`;
    case "stopped":
      return "The number on this booking replied STOP to the clinic. Only the patient can restart texts, by replying START to the clinic's number.";
    case "chart_inactive":
      return "The patient's chart is inactive, so nothing is sent.";
    case "declined":
      return `No ${PATIENT_MESSAGE_CHANNEL_PLURALS[channel]} are sent.`;
    default:
      return null;
  }
}

/** Why staff could not record "Patient agreed" or "Patient said no", in words for the desk. */
export function messagingDecisionRefusalText(
  detail: SchedulingErrorDetail | undefined,
): string | undefined {
  switch (detail) {
    case "messaging_notice_changed":
      return "The clinic published a new version of its text and email notice since you opened it. Read the new version to the patient, then record their answer again.";
    case "messaging_not_offered":
      return "The clinic doesn't send texts or emails right now, so an agreement can't be recorded. A refusal can.";
    default:
      return undefined;
  }
}
